// @ts-expect-error
import type {RouterHistory} from 'react-router-dom';

import {exhaustivenessCheck} from 'nutshell-core/typing/exhaustiveness-check';

import {ZoomIcon, MicrosoftTeamsIcon} from 'shells/icon';
import {NavIconAutomationIcon} from 'shells/icon/navigation/nav-icon-automation';
import {CalendarRegularReportIcon} from 'shells/icon/svg/calendar-regular-report-icon';
import {CalendarSquareRegularActivityIcon} from 'shells/icon/svg/calendar-square-regular-activity-icon';
import {ChartLineRegularReportIcon} from 'shells/icon/svg/chart-line-regular-report-icon';
import {ChatbotRegularIcon} from 'shells/icon/svg/chatbot-regular-icon';
// @ts-expect-error
import {DialpadIcon} from 'shells/icon/svg/dialpad-icon';
import {DripsRegularIcon} from 'shells/icon/svg/drips-regular-icon';
import {EnvelopesRegularIcon} from 'shells/icon/svg/envelopes-regular-icon';
import {GmailRegularIcon} from 'shells/icon/svg/gmail-regular-icon';
import {GoogleMeetIcon} from 'shells/icon/svg/google-meet-icon';
import {KixieIcon} from 'shells/icon/svg/kixie-icon';
import {LayoutRegularIcon} from 'shells/icon/svg/layout-regular-icon';
import {LeadChevronRegularIcon} from 'shells/icon/svg/lead-chevron-regular-icon';
import {MicrosoftOutlookRegularIcon} from 'shells/icon/svg/microsoft-outlook-regular-icon';
import {MobilePhoneRegularIcon} from 'shells/icon/svg/mobile-phone-regular-icon';
// @ts-expect-error
import {NewsletterIcon} from 'shells/icon/svg/newsletter-icon';
// @ts-expect-error
import {PencilToPaperIcon} from 'shells/icon/svg/pencil-to-paper-icon';
import {PeopleRegularIcon} from 'shells/icon/svg/people-regular-icon';
import {PhoneOutlineIcon} from 'shells/icon/svg/phone-outline-icon';
import {QuoteRegularIcon} from 'shells/icon/svg/quote-regular-icon';
import {VisitorIqIcon} from 'shells/icon/svg/visitor-iq-icon';
import type {Level} from 'shells/status-badge/status-badge';

import {OnboardingModuleType, OnboardingStepType} from 'nutshell-graphql-types';
import type {OnboardingModuleStatus} from 'nutshell-graphql-types';
import type {
    OnboardingModuleFragment,
    OnboardingStepFragment,
    UserEmailCalendarStatusesFragment,
    OnboardingQuotesFragment,
    UserVideoIntegrationsFragment,
} from 'nutshell-graphql-types';

import type {LearnMoreItem} from './types';

/**
 * Returns a tooltip string if the step cannot be completed, or undefined if it can.
 * This is used to determine if the step should be disabled with a tooltip explanation.
 */
export function getStepDisabledTooltip(
    step: OnboardingStepFragment,
    onboardingQuotes?: OnboardingQuotesFragment | null,
    hasPhoneConnected?: boolean
): string | null | undefined {
    if (step.isComplete) {
        return undefined;
    }

    switch (step.type) {
        case 'SEND_QUOTE': {
            if (!onboardingQuotes || !onboardingQuotes.openQuote) {
                return 'Create a new quote to begin';
            }

            return undefined;
        }
        case 'MAKE_PHONE_CALL': {
            if (!hasPhoneConnected) {
                return 'Connect your phone in order to make your first call';
            }

            return undefined;
        }
        case 'LOG_PHONE_ACTIVITY': {
            if (!hasPhoneConnected) {
                return 'Connect your phone in order to log your first call';
            }

            return undefined;
        }
        default:
            return undefined;
    }
}

export function getStatusBadgeLevel(status: OnboardingModuleStatus): Level {
    switch (status) {
        case 'TO_EXPLORE':
            return 'warning';
        case 'IN_PROGRESS':
            return 'info';
        case 'COMPLETED':
            return 'success';
        default:
            exhaustivenessCheck(status);
    }
}

export function getStatusBadgeText(status: OnboardingModuleStatus): string {
    switch (status) {
        case 'TO_EXPLORE':
            return 'To explore';
        case 'IN_PROGRESS':
            return 'In progress';
        case 'COMPLETED':
            return 'Completed';
        default:
            exhaustivenessCheck(status);
    }
}

export function getPercentComplete(module: OnboardingModuleFragment): number {
    if (module.steps.length === 0) {
        return 0.05;
    }

    let completedCount = 0;
    for (const step of module.steps) {
        if (step.isComplete) {
            completedCount++;
        }
    }

    const percentComplete = completedCount / module.steps.length;

    return Math.max(percentComplete, 0.05);
}

export function getIllustrationPath(type: OnboardingModuleType): string {
    switch (type) {
        case 'LEADS_PIPELINES':
            return 'trial-onboarding/trial-onboarding-leads-pipelines';
        case 'ESSENTIALS':
            return 'trial-onboarding/trial-onboarding-essentials';
        case 'CHATBOT_FORMS':
            return 'trial-onboarding/trial-onboarding-chatbot-forms';
        case 'EMAILS':
        case 'SEND_EMAILS_NON_ADMIN':
            return 'trial-onboarding/trial-onboarding-emails';
        case 'CALLS_MEETINGS':
        case 'CALLS_NON_ADMIN':
            return 'trial-onboarding/trial-onboarding-calls-meetings';
        case 'DATA_ORGANIZED':
        case 'STAY_ORGANIZED_NON_ADMIN':
            return 'trial-onboarding/trial-onboarding-data-organized';
        case 'QUOTES_INVOICES':
            return 'trial-onboarding/trial-onboarding-quotes-invoices';
        case 'MEETINGS_NON_ADMIN':
            return 'trial-onboarding/trial-onboarding-video-meetings';
        case 'SCHEDULER_NON_ADMIN':
            return 'trial-onboarding/trial-onboarding-scheduler';
        case OnboardingModuleType.USE_NUTSHELL_WHEREVER_NON_ADMIN:
            return 'trial-onboarding/trial-onboarding-mobile-apps';
        default:
            exhaustivenessCheck(type);
    }
}

export function getLearnMoreItems(
    moduleType: OnboardingModuleType,
    appHistory: RouterHistory,
    setIsEssentialsModalOpen: (isOpen: boolean) => void,
    onOpenVisitorIqOnboardingModal: () => void,
    onOpenEngagementBarOnboardingModal: () => void,
    onOpenBroadcastInfoModal: () => void,
    onOpenNewsletterInfoModal: () => void,
    onOpenDripSequenceInfoModal: () => void,
    onOpenPipelineOnboardingModal: () => void,
    onOpenQuotesInvoicesOnboardingModal: () => void
): Array<LearnMoreItem> {
    switch (moduleType) {
        case 'ESSENTIALS':
            return [
                {
                    description:
                        'Building blocks of Nutshell: companies, people, leads, and pipelines',
                    onClick: () => setIsEssentialsModalOpen(true),
                    icon: PeopleRegularIcon,
                },
            ];
        case 'LEADS_PIPELINES':
            return [
                {
                    description: 'What is a pipeline?',
                    onClick: () => onOpenPipelineOnboardingModal(),
                    icon: NavIconAutomationIcon,
                },
                {
                    description: '16 great sales process templates',
                    onClick: () => {
                        window.open('/dotcom/process-templates', '_blank');
                    },
                    icon: LeadChevronRegularIcon,
                },
            ];
        case 'CHATBOT_FORMS':
            return [
                {
                    description:
                        'Engagement bar: offer various ways for your website visitors to engage',
                    onClick: () => onOpenEngagementBarOnboardingModal(),
                    icon: ChatbotRegularIcon,
                },
                {
                    description: 'VisitorIQ: identify potential leads visiting your site',
                    onClick: () => onOpenVisitorIqOnboardingModal(),
                    icon: VisitorIqIcon,
                },
            ];
        case 'EMAILS':
        case 'SEND_EMAILS_NON_ADMIN':
            return [
                {
                    description:
                        'Broadcasts: design and send one-time email messages to an audience',
                    onClick: () => onOpenBroadcastInfoModal(),
                    icon: EnvelopesRegularIcon,
                },
                {
                    description: 'Newsletters: send regular correspondence to an audience',
                    onClick: () => onOpenNewsletterInfoModal(),
                    icon: NewsletterIcon,
                },
                {
                    description:
                        'Drip sequences: automate a series of messages to send at a set cadence',
                    onClick: () => onOpenDripSequenceInfoModal(),
                    icon: DripsRegularIcon,
                },
            ];
        case 'CALLS_MEETINGS':
            return [
                {
                    description: 'Create and log activities in Nutshell',
                    onClick: () => {
                        appHistory.replace({hash: '/create/activities'});
                    },
                    icon: CalendarSquareRegularActivityIcon,
                    buttonText: 'Try it out',
                },
                {
                    description: 'Activity report: see how your team is performing',
                    onClick: () => {
                        appHistory.push('/reports/activity');
                    },
                    icon: CalendarRegularReportIcon,
                },
            ];
        case 'DATA_ORGANIZED':
            return [
                {
                    description:
                        'Custom fields: organize your data across contacts, companies, and leads',
                    onClick: () => {
                        window.open(
                            'https://support.nutshell.com/en/articles/8429015-how-to-create-and-manage-custom-fields',
                            '_blank'
                        );
                    },
                    icon: PencilToPaperIcon,
                },
            ];
        case 'QUOTES_INVOICES':
            return [
                {
                    description: 'Working with quotes & invoices',
                    onClick: () => onOpenQuotesInvoicesOnboardingModal(),
                    icon: QuoteRegularIcon,
                },
            ];
        case 'STAY_ORGANIZED_NON_ADMIN':
            // TODO: Add learn more items once they're defined
            return [
                {
                    description: 'Work with lists of contacts',
                    onClick: () => {
                        window.open(
                            'https://support.nutshell.com/en/articles/8428926-crm-records-working-with-lists-of-contacts',
                            '_blank'
                        );
                    },
                    icon: PeopleRegularIcon,
                },
                {
                    description: 'Nutshell reports',
                    onClick: () => {
                        window.open(
                            'https://support.nutshell.com/en/articles/8428976-reporting-an-overview-of-nutshell-reports',
                            '_blank'
                        );
                    },
                    icon: ChartLineRegularReportIcon,
                },

                {
                    description: 'Customize your dashboard',
                    onClick: () => {
                        window.open(
                            'https://support.nutshell.com/en/articles/8429123-sales-dashboard-customize-your-dashboard',
                            '_blank'
                        );
                    },
                    icon: LayoutRegularIcon,
                },
            ];
        case 'MEETINGS_NON_ADMIN':
            return [
                {
                    description: 'Connecting with Zoom',
                    onClick: () => {
                        window.open('https://www.nutshell.com/integrations/zoom', '_blank');
                    },
                    icon: ZoomIcon,
                },
                {
                    description: 'Connecting with Google Meet',
                    onClick: () => {
                        window.open('https://www.nutshell.com/integrations/google-meet', '_blank');
                    },
                    icon: GoogleMeetIcon,
                },
                {
                    description: 'Connecting with Microsoft Teams',
                    onClick: () => {
                        window.open(
                            'https://www.nutshell.com/integrations/microsoft-teams',
                            '_blank'
                        );
                    },
                    icon: MicrosoftTeamsIcon,
                },
            ];
        case 'SCHEDULER_NON_ADMIN':
            return [
                {
                    description: 'Scheduler: plan, schedule, and manage meetings',
                    onClick: () => {
                        window.open(
                            'https://support.nutshell.com/en/articles/8428989-scheduler-plan-schedule-and-manage-meetings',
                            '_blank'
                        );
                    },
                    icon: CalendarSquareRegularActivityIcon,
                },
            ];
        case 'CALLS_NON_ADMIN':
            return [
                {
                    description: 'What is click-to-call?',
                    onClick: () => {
                        window.open(
                            'https://support.nutshell.com/en/articles/8428904-phone-setting-up-click-to-call',
                            '_blank'
                        );
                    },
                    icon: PhoneOutlineIcon,
                },
                {
                    description: 'Connecting with Kixie',
                    onClick: () => {
                        window.open(
                            'https://support.nutshell.com/en/articles/8429080-connecting-kixie-to-nutshell',
                            '_blank'
                        );
                    },
                    icon: KixieIcon,
                },
                {
                    description: 'Connecting with Dialpad',
                    onClick: () => {
                        window.open(
                            'https://support.nutshell.com/en/articles/13006189-integrations-connecting-dialpad-to-nutshell',
                            '_blank'
                        );
                    },
                    icon: DialpadIcon,
                },
            ];
        case OnboardingModuleType.USE_NUTSHELL_WHEREVER_NON_ADMIN:
            return [
                {
                    description: "Explore Nutshell's iOS app",
                    onClick() {
                        window.open(
                            'https://support.nutshell.com/en/articles/8428966-mobile-devices-explore-nutshell-s-ios-app',
                            '_blank'
                        );
                    },
                    icon: MobilePhoneRegularIcon,
                },
                {
                    description: "Explore Nutshell's Android app",
                    onClick: () => {
                        window.open(
                            'https://play.google.com/store/apps/details?id=com.nutshell.android',
                            '_blank'
                        );
                    },
                    icon: MobilePhoneRegularIcon,
                },
                {
                    description: 'How to install the Chrome extension for Gmail',
                    onClick: () => {
                        window.open(
                            'https://support.nutshell.com/en/articles/8428899-how-to-install-the-chrome-extension-for-gmail',
                            '_blank'
                        );
                    },
                    icon: GmailRegularIcon,
                },
                {
                    description: 'How to install the Microsoft Outlook Add-In',
                    onClick: () => {
                        window.open(
                            'https://support.nutshell.com/en/articles/8428902-how-to-install-the-microsoft-outlook-add-in',
                            '_blank'
                        );
                    },
                    icon: MicrosoftOutlookRegularIcon,
                },
            ];
        default:
            exhaustivenessCheck(moduleType);
    }
}

export function getOnboardingStepClickHandler(
    step: OnboardingStepFragment,
    userEmailCalendarStatuses: UserEmailCalendarStatusesFragment | null | undefined,
    userVideoIntegrations: UserVideoIntegrationsFragment | null | undefined,
    appHistory: RouterHistory,
    onOpenInviteUsersModal: () => void,
    onOpenImportModal: () => void,
    setIsCreateSchedulerMeetingModalOpen: (isOpen: boolean) => void,
    setIsAddingNewBroadcast: (isOpen: boolean) => void,
    setIsCreatingPipeline: (isOpen: boolean) => void,
    setIsConnectMeetingModalOpen: (isOpen: boolean) => void,
    onboardingQuotes?: OnboardingQuotesFragment | null,
    hasPhoneIntegrationConnected?: boolean
): (() => void) | null | undefined {
    switch (step.type) {
        case 'EMAIL_SYNC':
        case 'EMAIL_CALENDAR_SYNC':
            if (step.isComplete) {
                return () => {
                    appHistory.push('/my-account/email-sending');
                };
            }
            return undefined;
        case 'EMAIL_SEQUENCE':
            return () => {
                appHistory.push('/sales/email/templates');
            };
        case 'IMPORT_CRM':
            return () => {
                onOpenImportModal();
            };
        case 'INVITE_TEAM':
            return () => {
                onOpenInviteUsersModal();
            };
        case 'MOVE_LEAD': {
            if (step.isComplete) {
                return () => {
                    appHistory.push('/leads');
                };
            }

            return () => {
                appHistory.push({
                    pathname: '/leads',
                    search: '?restoreDefaults=true',
                    state: {tour: 'lead-board-tour'},
                });
            };
        }
        case 'DNS_SETUP':
            return () => {
                appHistory.push('/setup/domains');
            };
        case 'DESIGN_BROADCAST':
            return () => {
                setIsAddingNewBroadcast(true);
            };
        case 'SCHEDULER':
            if (step.isComplete) {
                return () => {
                    appHistory.push('/sales/scheduler');
                };
            }

            return () => {
                setIsCreateSchedulerMeetingModalOpen(true);
            };
        case 'PREVIEW_CHATBOT':
            return () => {
                appHistory.push('/inbox/settings/chat-onboarding');
            };
        case 'NUTSHELL_FORM':
            if (step.isComplete) {
                return () => {
                    appHistory.push('/marketing/forms');
                };
            }

            return () => {
                appHistory.replace({hash: '/create/forms'});
            };
        case 'CHAT_TO_SITE':
            return () => {
                appHistory.push('/setup/tracking-snippet');
            };
        case 'VISITOR_IQ':
            return () => {
                appHistory.push('/marketing/visitors');
            };
        case 'PHONE_PROVIDER':
        case 'CONNECT_PHONE':
            if (step.isComplete) {
                return () => {
                    appHistory.push('/my-account/phone');
                };
            }

            return () => {
                appHistory.replace({hash: '#/connect-phone-integration/get-started'});
            };
        case 'CLICK_TO_CALL':
            if (step.isComplete) {
                return () => {
                    appHistory.push('/my-account/phone');
                };
            }

            return () => {
                appHistory.replace({
                    hash: '#/connect-phone-integration/get-started?integrationType=CLICKTOCALL',
                });
            };
        case 'CONNECT_MEETING':
            if (step.isComplete) {
                return undefined;
            }

            return () => {
                setIsConnectMeetingModalOpen(true);
            };
        case 'CREATE_PIPELINE':
            if (step.isComplete) {
                return () => {
                    appHistory.push('/sales/pipelines');
                };
            }

            return () => {
                setIsCreatingPipeline(true);
            };
        case 'ADD_LEAD':
            return () => {
                appHistory.replace({hash: '/create/leads'});
            };
        case 'REPORT_LEADS':
            if (step.isComplete) {
                return () => {
                    appHistory.push('/reports/overview');
                };
            }

            return () => {
                appHistory.push({
                    pathname: '/reports/overview',
                    state: {tour: 'lead-report-tour'},
                });
            };
        case 'PRODUCTS_ADDED':
            return () => {
                appHistory.push('/sales/product');
            };
        case 'CUSTOM_FIELD':
            if (step.isComplete) {
                return () => {
                    appHistory.push('/setup/custom-field/lead');
                };
            }

            return () => {
                appHistory.push('/setup/custom-field/lead?create');
            };
        case 'SAVED_LISTS':
            if (step.isComplete) {
                return () => {
                    appHistory.push('/leads');
                };
            }

            return () => {
                appHistory.push({
                    pathname: '/leads',
                    search: '?restoreDefaults=true',
                    state: {tour: 'saved-list-tour'},
                });
            };
        case 'QUOTE_TEMPLATE':
            return () => {
                appHistory.push('/sales/quote-template');
            };
        case 'CREATE_QUOTE':
            return () => {
                appHistory.replace({hash: '/create/quotes', state: {modal: true}});
            };
        case 'SEND_QUOTE': {
            if (!onboardingQuotes || !onboardingQuotes.openQuote || step.isComplete) {
                return undefined;
            }

            const openQuoteModalUrl = onboardingQuotes.openQuote.modalUrl;

            return () => {
                appHistory.replace({
                    hash: openQuoteModalUrl,
                    state: {modal: true},
                });
            };
        }
        case 'CREATE_INVOICE': {
            return () => {
                appHistory.replace({
                    hash: '/create/invoices',
                    state: {modal: true},
                });
            };
        }
        // TODO: Update these to be a tour or modal type of onboarding
        case 'SEND_EMAIL':
            return () => {
                appHistory.push('/my-account/email-sending');
            };
        case 'SEND_EMAIL_SEQUENCE':
            return () => {
                appHistory.push('/my-account/email-sending');
            };
        case 'CREATE_DASHBOARD_CARD':
            return () => {
                appHistory.push('/sales/dashboard#add-dashboard-card');
            };
        case 'VIEW_ACTIVITIES':
            if (step.isComplete) {
                return () => {
                    appHistory.push('/sales/dashboard?tab=activities');
                };
            }

            return () => {
                appHistory.push({
                    pathname: '/sales/dashboard',
                    search: '?tab=activities&resetActivities',
                    state: {tour: 'view-activities-tour'},
                });
            };
        case 'SYNC_VIDEO_CALL': // Same as 'LOG_VIDEO_ACTIVITY'
        case 'LOG_VIDEO_ACTIVITY':
            if (userVideoIntegrations?.zoomIntegration?.isEnabled) {
                return () => {
                    appHistory.push('/sales/meetings/zoom/unlogged-meetings');
                };
            }

            if (userVideoIntegrations?.googleMeetIntegration?.isEnabledMeetSync) {
                return () => {
                    appHistory.push('/sales/meetings/meet/unlogged-meetings');
                };
            }

            if (userVideoIntegrations?.microsoftTeamsIntegration?.isEnabled) {
                return () => {
                    appHistory.push('/sales/meetings/teams/unlogged-meetings');
                };
            }

            return () => {
                appHistory.push('/sales/meetings/zoom/unlogged-meetings');
            };
        case 'BOOK_MEETING':
            return () => {
                appHistory.push('/sales/scheduler');
            };
        case 'MAKE_PHONE_CALL':
        case 'LOG_PHONE_ACTIVITY':
            if (hasPhoneIntegrationConnected) {
                return () => {
                    appHistory.push('/sales/calls/unlogged-meetings');
                };
            }

            return undefined;
        case OnboardingStepType.CREATE_ACCOUNT:
        case OnboardingStepType.EMAIL_LEADS:
        case OnboardingStepType.SAVED_LIST_DASHBOARD:
            return undefined;
        case OnboardingStepType.INSTALL_MOBILE_APP:
            return () => {
                window.open(
                    'https://support.nutshell.com/en/collections/11432989-mobile-devices',
                    '_blank'
                );
            };
        case OnboardingStepType.INSTALL_CHROME_OR_OUTLOOK:
            return () => {
                window.open('https://www.nutshell.com/crm/email-calendar-sync', '_blank');
            };
        default:
            exhaustivenessCheck(step.type);
    }
}
